import api from '@/lib/api/axios'
import type { ListParams, PaginatedResponse } from '@/lib/api/types'

export interface RecoverRequest {
    id: number | null
    requestID: string | null
    objectID: string | null
    objectDescription: string | null
    objectversionsID: number | null
    scenario: number | null
    versionMetadata: string | null
    versionAnalytics: number | null
    requestDateTime: string | Date | null
    location: string | null
    teamName: string | null
    responsibleTeamId: number | null
    forecastStartTime: string | Date | null
    objectReady: number | null
    waitGo: string | null
    requestObjectCount: number
    iconFilename: string | null
}

export interface RecoverRequestDetail {
    requestID: string | null
    objectID: string | null
    tier: string | null
    scenario: number | null
    objectversionsID: number | null
    versionMetadata: string | null
    versionAnalytics: number | null
    versionSize: number | null
    requestDateTime: string | Date | null
    main: string | null
}

export interface RequestIDOption {
    requestID: string | null
    objectID: string | null
    requestDate: string | Date | null
    scenarioName: string | null
}

export interface RecoverRequestStats {
    uniqueRequestCount: number
    objectCount: number
    locationCount: number
}

export interface RowScenarioInfo {
    scenario: {
        scenarioName: string | null
        description: string | null
        useCase: string | null
    } | null
    requestSets: { objectID: string | null; requestDate: string | Date | null }[]
}

export interface RecoveryAssignment {
    id: string | null
    recoverRequestsID: number | null
    userID: number | null
    userName: string | null
    action: string | null
    actiondatetime: string | null
    update: string | null
    attachments: string | null
}

export interface RunbookAttachment {
    name: string
    url: string
    type: string
    size?: number
}

export interface RecoveryDependencyProgressRow {
    SourceID: string | null
    Engineer: string | number | null
    EngineerName: string | null
    WaitGo: string | null
    PercentComplete: number | null
    ForecastStartTime: string | Date | null
    ForecastFinishTime: string | Date | null
    AssignmentStatus: string | null
    IconFilename: string | null
}

export const recoverRequestsApi = {
    list: (params?: ListParams & { requestID?: string; location?: string; team?: string; recoverySet?: string; scenario?: string }) => {
        const queryParams = params ? {
            ...params,
            limit: params.perPage || params.limit || 20,
            perPage: undefined,
        } : undefined
        if (queryParams) {
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key as keyof typeof queryParams] === undefined) {
                    delete queryParams[key as keyof typeof queryParams]
                }
            })
        }
        return api.get<PaginatedResponse<RecoverRequest>>('/api/recover-requests', { params: queryParams })
    },
    getRequestIDOptions: () => api.get<{ success: boolean; data: RequestIDOption[] }>('/api/recover-requests/filter-options/request-ids'),
    getLocationOptions: () => api.get<{ success: boolean; data: string[] }>('/api/recover-requests/filter-options/locations'),
    getTeamOptions: () => api.get<{ success: boolean; data: string[] }>('/api/recover-requests/filter-options/teams'),
    getRecoverySetOptions: () => api.get<{ success: boolean; data: string[] }>('/api/recover-requests/filter-options/recovery-sets'),
    getScenarioOptions: () => api.get<{ success: boolean; data: { id: number; name: string }[] }>('/api/recover-requests/filter-options/scenarios'),
    getRowInfo: (requestID: string | null, scenario: number | null) => {
        const params: { requestID?: string; scenario?: string } = {}
        if (requestID) params.requestID = requestID
        if (scenario != null) params.scenario = String(scenario)
        return api.get<{ success: boolean; data: RowScenarioInfo }>('/api/recover-requests/row-info', { params: Object.keys(params).length ? params : undefined })
    },
    getByObjectID: (objectID: string, requestID?: string) => {
        const url = requestID 
            ? `/api/recover-requests/by-object/${encodeURIComponent(objectID)}?requestID=${encodeURIComponent(requestID)}`
            : `/api/recover-requests/by-object/${encodeURIComponent(objectID)}`
        return api.get<{ success: boolean; data: RecoverRequestDetail[] }>(url)
    },
    getStats: () => api.get<{ success: boolean; data: RecoverRequestStats }>('/api/recover-requests/stats'),
    getAssignableUsers: (objectID?: string, responsibleTeamId?: number, locationId?: number | null) => {
        const params: any = {}
        if (objectID) params.objectID = objectID
        if (responsibleTeamId) params.responsibleTeamId = responsibleTeamId
        if (locationId) params.locationId = locationId
        console.log('🔍 DEBUG getAssignableUsers API call:', {
            objectID,
            responsibleTeamId,
            locationId,
            responsibleTeamIdType: typeof responsibleTeamId,
            locationIdType: typeof locationId,
            params
        })
        return api.get<{ success: boolean; data: any[] }>('/api/recover-requests/assignable-users', { params: Object.keys(params).length > 0 ? params : undefined })
    },
    assignEngineer: (requestID: number | null, userID: string | number) => {
        console.log('🔍 DEBUG assignEngineer API call:', {
            requestID,
            requestIDType: typeof requestID,
            userID,
            userIDType: typeof userID,
            payload: { requestID, userID }
        })
        return api.post<{ success: boolean; data: { requestID: number; userID: number; assignedAt: string } }>('/api/recover-requests/assign-engineer', {
            requestID,
            userID
        })
    },
    unassignEngineer: (requestID: number | null) => {
        return api.post<{ success: boolean; data: { requestID: number; unassignedAt: string } }>('/api/recover-requests/unassign-engineer', {
            requestID,
        })
    },
    markComplete: (requestID: number) => {
        return api.post<{ success: boolean; data: { requestId: number } }>(`/api/recover-requests/${requestID}/mark-complete`)
    },
    startRecovery: (requestID: number, objectID: string, scenario: number) => {
        return api.post<{ success: boolean; data: { requestID: number; taskCount: number } }>('/api/recover-requests/start-recovery', {
            requestID,
            objectID,
            scenario,
        })
    },
    getRecoveryAssignments: (requestID: number | string) => {
        return api.get<{ success: boolean; data: { assignments: RecoveryAssignment[]; updateTimeRemainingMinutes: number | null } }>(`/api/recover-requests/${encodeURIComponent(String(requestID))}/recovery-assignments`)
    },
    addRecoveryAssignment: (requestID: number, payload: { action?: string; update: string; attachments?: RunbookAttachment[] }) => {
        return api.post<{ success: boolean; data: { requestID: number; action: string; addedAt: string } }>(`/api/recover-requests/${requestID}/recovery-assignments`, payload)
    },
    generateEngineerUpdate: (requestID: number, context: { objectId: string; objectDescription: string; tasks: any[]; previousUpdates: any[] }) => {
        return api.post<{ success: boolean; data: { html: string } }>(`/api/recover-requests/${requestID}/generate-update`, context)
    },
    getRecoveryRunbook: (requestID: number | string) => {
        return api.get<{ success: boolean; data: any[] }>(`/api/recover-requests/${encodeURIComponent(String(requestID))}/recovery-runbook`)
    },
    getRecoveryDependencyProgress: (mainId: string) => {
        return api.get<{ success: boolean; data: RecoveryDependencyProgressRow[] }>(`/api/recover-requests/dependency-progress/${encodeURIComponent(mainId)}`)
    },
    updateRecoveryRunbook: (requestID: number | string, tasks: Array<{ id: number; duration?: number; taskOffset?: number; taskStatus?: string; taskOwner?: string; actualStart?: string; actualFinish?: string; actualDuration?: number; forecastDuration?: number; forecastTaskOffset?: number }>) => {
        return api.patch<{ success: boolean; data: { updated: number } }>(`/api/recover-requests/${encodeURIComponent(String(requestID))}/recovery-runbook`, { tasks })
    },
    // Mobilisation workflow (step completion from backend SQL; actions call SP/endpoints)
    getMobilisationStatus: (requestID: number) =>
        api.get<{ success: boolean; data: { completedSteps: number; totalSteps: 5 } }>(`/api/recover-requests/${requestID}/mobilisation/status`),
    getMobilisationTeams: (requestID: number) =>
        api.get<{ success: boolean; data: MobilisationTeam[] }>(`/api/recover-requests/${requestID}/mobilisation/teams`),
    mobiliseUsers: (requestID: number) =>
        api.post<{ success: boolean; data?: unknown }>(`/api/recover-requests/${requestID}/mobilisation/activate-users`, {}),
    sendTeamNotification: (requestID: number, teamId: number, channel: 'sms' | 'email') =>
        api.post<{ success: boolean }>(`/api/recover-requests/${requestID}/mobilisation/notify`, { teamId, channel }),
    sendAllNotifications: (requestID: number) =>
        api.post<{ success: boolean }>(`/api/recover-requests/${requestID}/mobilisation/notify-all`, {}),
    getVersionUrls: (requestID: number | string) =>
        api.get<{ success: boolean; data: VersionUrl[] }>(`/api/recover-requests/${encodeURIComponent(String(requestID))}/version-urls`),

    requestPreSignedUrls: (requestID: number | string) =>
        api.post(`/api/recover-requests/${encodeURIComponent(String(requestID))}/request-presigned-urls`),
}

export interface VersionUrl {
    id: number
    fileName: string | null
    preSignedUrl: string | null
    expiryDateTime: string | null
    fileSize: number | null
}

// Recovery Sets (Recovery Manager: sets + unassigned request IDs)
export interface RecoverySetSummary {
    id: number
    name: string
    /** ISO date string when the set was created */
    created: string | null
    requestIdCount: number
    mobilisationCompletedSteps: number
    mobilisationTotalSteps: number
    mobilisationPercent?: number
    completionPercent?: number
    /** Runbook progress (0–100), from RecoverySetStatus.RunbookPercentComplete */
    runbookPercentComplete?: number | null
    technicalChecklistPercent?: number
    assigned: number
    inProgress: number
    issue: number
    completed: number
    total: number
    /** From RecoverySetStatus.NoEngineers */
    noEngineers?: number
    /** From RecoverySetStatus.NoAvailable */
    noAvailable?: number
    startTime: string | null
    completedTime: string | null
}

export interface RecoverySetDetail extends RecoverySetSummary {
    recoveryInformation: string | null
    testing: number
    updatetime: string | null
    /** From RecoverySetStatus.RunbookPercentComplete (aggregated for dashboard doughnut) */
    runbookPercentComplete?: number | null
}

export interface UnassignedRequestRow {
    id: number
    requestID: string
    objectID: string | null
    objectDescription?: string | null
    iconFilename?: string | null
    scenario?: number | null
    scenarioName: string | null
    /** From RecoveryRequestStatus; optional when row is from in-set list */
    noObjects?: number | null
    noComplete?: number | null
    noGo?: number | null
    forecastStartTime: string | null
    forecastFinishTime?: string | null
    totalTasks?: number | null
    completedTasks?: number | null
    startedTasks?: number | null
    issueTasks?: number | null
    overallPercent?: number | null
    recoveryStatus?: string | null
    /** Present when row is from in-set list */
    requestDateTime?: string | null
    /** Present when row is from in-set list (object count for request) */
    objectCount?: number
}

export interface CreateRecoverySetPayload {
    name: string
    recoveryInformation?: string
}

export interface DashboardSummary {
    percentActivated: number
    noMessagesSent: number
    noRed: number
}

export interface DashboardStatistics {
    mobPercentComplete: number
    taskPercentComplete: number
    noImpacted: number
}

export interface HierarchyForecastRow {
    recoveryHierarchyId: number | null
    requestID: string | null
    recoverRequestID?: number | null
    objectID: string | null
    objectDescription?: string | null
    waitGo: string | null
    forecastStartTime: string | null
    forecastFinishTime: string | null
    isComplete: number | null
    actualStartTime: string | null
    totalTasks: number | null
    completedTasks: number | null
    startedTasks: number | null
    issueTasks: number | null
    percentComplete: number | null
    engineerName: string | null
    assignmentStatus: string | null
    iconFilename?: string | null
}

export interface RequestObjectDetailRow {
    recoveryHierarchyId: number | null
    requestID: string | null
    recoverRequestID?: number | null
    objectID: string | null
    objectDescription?: string | null
    iconFilename?: string | null
    vaultedDateTime: string | null
    waitGo: string | null
    forecastStartTime: string | null
    forecastFinishTime: string | null
    isComplete: number | null
    actualStartTime: string | null
    totalTasks: number | null
    completedTasks: number | null
    startedTasks: number | null
    issueTasks: number | null
    percentComplete: number | null
    engineerName: string | null
    assignmentStatus: string | null
    analytics?: number | null
    location?: number | null
    locationDescription?: string | null
}

// ── Location Tracking (read-only world-clock + engineer feed) ──────────────
export interface RecoveryLocation {
    id: number
    name: string
    timeZone: string
    objectCount: number
    assignedEngineerCount: number
    requestId: string | null
    recoverySet: number
}
export interface RecoveryObject {
    id: string
    locationId: number
    city: string
    description: string
    icon: string | null
    status: string | null
    engineer: string | null
    tasksTotal: number
    tasksCompleted: number
    tasksStarted: number
}
export interface LocationTrackingData {
    locations: RecoveryLocation[]
    objects: RecoveryObject[]
    scope: { option: string; requestId: string | null; recoverySet: number }
}

export const recoverySetsApi = {
    list: () => api.get<{ success: boolean; data: RecoverySetSummary[] }>('/api/recovery-sets'),
    getLocationTracking: (setId: number, requestId?: string) =>
        api.get<{ success: boolean; data: LocationTrackingData }>(
            `/api/recovery-sets/${setId}/location-tracking`,
            { params: requestId ? { requestId } : undefined }
        ),
    getDashboard: () =>
        api.get<{
            success: boolean
            data: { summary: DashboardSummary; statisticsBySetId: Record<number, DashboardStatistics> }
        }>('/api/recovery-sets/dashboard'),
    create: (payload: CreateRecoverySetPayload) =>
        api.post<{ success: boolean; data: RecoverySetSummary }>('/api/recovery-sets', payload),
    get: (setId: number) => api.get<{ success: boolean; data: RecoverySetDetail }>(`/api/recovery-sets/${setId}`),
    getRequestIdsInSet: (setId: number) =>
        api.get<{ success: boolean; data: UnassignedRequestRow[] }>(`/api/recovery-sets/${setId}/request-ids`),
    addRequestIdToSet: (setId: number, requestID: string, scenario?: number | null) =>
        api.post<{ success: boolean; data: { requestID: string; added: boolean } }>(`/api/recovery-sets/${setId}/request-ids`, { requestID, scenario: scenario ?? undefined }),
    checkAddRequestConflicts: (setId: number, requestId: string) =>
        api.post<{ success: boolean; data: { status: 'clean' } | { status: 'conflicts'; conflicts: Array<{ objectId: string; objectName: string; options: Array<{ requestId: string | null; objectversionsId: number | null; vaultDatetime: string | null; analytics: number | null; forecastStatus?: { inProgress: boolean; totalTasks?: number | null; startedTasks?: number | null; completedTasks?: number | null; issueTasks?: number | null; percentComplete?: number | null; engineerName?: string | null; forecastFinishTime?: string | null } }> }> } }>(`/api/recovery-sets/${setId}/requests/check`, { requestId }),
    addRequestToSetWithResolutions: (setId: number, payload: { requestId: string; userId?: number; resolutions: Array<{ objectId: string; priorityRequestId: string }> }) =>
        api.post<{ success: boolean; data: { requestID: string; added: boolean; requestIdCount?: number } }>(`/api/recovery-sets/${setId}/requests`, payload),
    removeRequestIdFromSet: (setId: number, requestID: string) =>
        api.delete<{ success: boolean; data: { requestID: string; removed: boolean } }>(`/api/recovery-sets/${setId}/request-ids/${encodeURIComponent(requestID)}`),
    getUnassignedRequests: (params?: { page?: number; limit?: number }) => {
        const p = params || {}
        return api.get<{ success: boolean; data: UnassignedRequestRow[]; total: number; page: number; perPage: number }>('/api/recovery-sets/unassigned-requests/list', { params: p })
    },
    getHierarchyForecast: (requestID: string) =>
        api.get<{ success: boolean; data: HierarchyForecastRow[] }>(`/api/recovery-sets/unassigned-requests/${encodeURIComponent(requestID)}/hierarchy-forecast`),
    getRequestObjectDetails: (requestID: string) =>
        api.get<{ success: boolean; data: RequestObjectDetailRow[] }>(`/api/recovery-sets/unassigned-requests/${encodeURIComponent(requestID)}/object-details`),
    getSetObjectDetails: (setId: number) =>
        api.get<{ success: boolean; data: RequestObjectDetailRow[] }>(`/api/recovery-sets/${setId}/objects`),
    getSetRequestObjectDetails: (setId: number, requestID: string) =>
        api.get<{ success: boolean; data: RequestObjectDetailRow[] }>(`/api/recovery-sets/${setId}/request-ids/${encodeURIComponent(requestID)}/object-details`),
    getMobilisationStatus: (setId: number) =>
        api.get<{ success: boolean; data: { completedSteps: number; totalSteps: number } }>(`/api/recovery-sets/${setId}/mobilisation/status`),
    getMobilisationTeams: (setId: number) =>
        api.get<{ success: boolean; data: MobilisationTeam[] }>(`/api/recovery-sets/${setId}/mobilisation/teams`),
    mobiliseUsers: (setId: number) =>
        api.post<{ success: boolean; data?: unknown }>(`/api/recovery-sets/${setId}/mobilisation/activate-users`, {}),
    deactivateUsers: (setId: number) =>
        api.post<{ success: boolean; data?: unknown }>(`/api/recovery-sets/${setId}/mobilisation/deactivate-users`, {}),
    activateTeamUsers: (setId: number, teamId: number) =>
        api.post<{ success: boolean; data?: unknown }>(`/api/recovery-sets/${setId}/mobilisation/activate-team`, { teamId }),
    deactivateTeamUsers: (setId: number, teamId: number) =>
        api.post<{ success: boolean; data?: unknown }>(`/api/recovery-sets/${setId}/mobilisation/deactivate-team`, { teamId }),
    sendTeamNotification: (setId: number, teamId: number, channel: 'sms' | 'email') =>
        api.post<{ success: boolean }>(`/api/recovery-sets/${setId}/mobilisation/notify`, { teamId, channel }),
    /**
     * Send to one team (one channel) with NDJSON stream for progress. Use when onProgress is provided.
     * Returns { sent, failed, errorMessage? } on done.
     */
    sendTeamNotificationStream: async (
        setId: number,
        teamId: number,
        channel: 'sms' | 'email',
        options: {
            onProgress?: (sent: number, total: number) => void
            token?: string | null
        }
    ): Promise<{ sent: number; failed: number; errorMessage?: string }> => {
        const base = (import.meta as any).env?.VITE_API_BASE_URL ?? ''
        const url = `${base}/api/recovery-sets/${setId}/mobilisation/notify`
        const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Stream-Progress': 'true' }
        if (options.token) headers['Authorization'] = `Bearer ${options.token}`
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ teamId, channel }) })
        if (!res.ok) {
            const text = await res.text()
            throw new Error(text || `HTTP ${res.status}`)
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response body')
        const dec = new TextDecoder()
        let buf = ''
        let donePayload: { sent: number; failed: number; errorMessage?: string } | null = null
        while (true) {
            const { value, done } = await reader.read()
            if (done) break
            buf += dec.decode(value, { stream: true })
            const lines = buf.split('\n')
            buf = lines.pop() ?? ''
            for (const line of lines) {
                const trimmed = line.trim()
                if (!trimmed) continue
                try {
                    const event = JSON.parse(trimmed) as { type: string; sent?: number; total?: number; failed?: number; errorMessage?: string }
                    if (event.type === 'progress' && options.onProgress && typeof event.sent === 'number' && typeof event.total === 'number') {
                        options.onProgress(event.sent, event.total)
                    }
                    if (event.type === 'done') {
                        donePayload = {
                            sent: event.sent ?? 0,
                            failed: event.failed ?? 0,
                            errorMessage: typeof event.errorMessage === 'string' ? event.errorMessage : undefined,
                        }
                    }
                } catch (_) {}
            }
        }
        if (donePayload == null) throw new Error('Stream ended without done event')
        return donePayload
    },
    sendAllNotifications: (setId: number) =>
        api.post<{ success: boolean }>(`/api/recovery-sets/${setId}/mobilisation/notify-all`, {}),
    /**
     * Send to all teams and consume NDJSON stream for progress updates.
     * Calls onProgress for each progress event; resolves with { sent, failed } on done.
     */
    sendAllNotificationsStream: async (
        setId: number,
        options: {
            onProgress?: (event: NotifyAllProgressEvent) => void
            token?: string | null
        }
    ): Promise<{ sent: number; failed: number }> => {
        const base = (import.meta as any).env?.VITE_API_BASE_URL ?? ''
        const url = `${base}/api/recovery-sets/${setId}/mobilisation/notify-all`
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (options.token) headers['Authorization'] = `Bearer ${options.token}`
        const res = await fetch(url, { method: 'POST', headers, body: '{}' })
        if (!res.ok) {
            const text = await res.text()
            throw new Error(text || `HTTP ${res.status}`)
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response body')
        const dec = new TextDecoder()
        let buf = ''
        let donePayload: { sent: number; failed: number } | null = null
        while (true) {
            const { value, done } = await reader.read()
            if (done) break
            buf += dec.decode(value, { stream: true })
            const lines = buf.split('\n')
            buf = lines.pop() ?? ''
            for (const line of lines) {
                const trimmed = line.trim()
                if (!trimmed) continue
                try {
                    const event = JSON.parse(trimmed) as { type: string; message?: string; sent?: number; failed?: number; teamName?: string; teamIndex?: number; totalTeams?: number; totalSentSoFar?: number; totalFailedSoFar?: number }
                    if (event.type === 'error') throw new Error(event.message ?? 'Stream error')
                    if (event.type === 'progress' && options.onProgress) {
                        options.onProgress({
                            type: 'progress',
                            teamName: event.teamName,
                            teamIndex: event.teamIndex ?? 0,
                            totalTeams: event.totalTeams ?? 0,
                            totalSentSoFar: event.totalSentSoFar ?? 0,
                            totalFailedSoFar: event.totalFailedSoFar ?? 0,
                        })
                    }
                    if (event.type === 'done') {
                        donePayload = { sent: event.sent ?? 0, failed: event.failed ?? 0 }
                    }
                } catch (e) {
                    if (e instanceof Error && e.message !== 'Stream error') throw e
                    throw e
                }
            }
        }
        if (donePayload == null) throw new Error('Stream ended without done event')
        return donePayload
    },
    getTaskActions: (setId: number, recoveryMobilisationId: number) =>
        api.get<{ success: boolean; data: MobilisationTaskAction[] }>(
            `/api/recovery-sets/${setId}/mobilisation/tasks/${recoveryMobilisationId}/actions`
        ),
    saveTaskAction: (setId: number, recoveryMobilisationId: number, payload: { action: string; comments?: string }) =>
        api.post<{ success: boolean; data: MobilisationTaskAction }>(
            `/api/recovery-sets/${setId}/mobilisation/tasks/${recoveryMobilisationId}/actions`,
            payload
        ),
    getTechnicalTaskActions: (setId: number, recoveryTaskId: number) =>
        api.get<{ success: boolean; data: TechnicalTaskAction[] }>(
            `/api/recovery-sets/${setId}/technical-checklist/tasks/${recoveryTaskId}/actions`
        ),
    saveTechnicalTaskAction: (setId: number, recoveryTaskId: number, payload: { action: string; comments?: string }) =>
        api.post<{ success: boolean; data: TechnicalTaskAction }>(
            `/api/recovery-sets/${setId}/technical-checklist/tasks/${recoveryTaskId}/actions`,
            payload
        ),
    getImpactRecoveryObjects: (setId: number, params?: { search?: string; location?: number | null; team?: number | null }) =>
        api.get<{ success: boolean; data: ImpactRecoveryObjectRow[] }>(`/api/recovery-sets/${setId}/impact/recovery-objects`, { params: params ?? {} }),
    getImpactLocationOptions: (setId: number) =>
        api.get<{ success: boolean; data: { id: number; name: string }[] }>(`/api/recovery-sets/${setId}/impact/filter-options/locations`),
    getImpactTeamOptions: (setId: number) =>
        api.get<{ success: boolean; data: { id: number; name: string }[] }>(`/api/recovery-sets/${setId}/impact/filter-options/teams`),
    updateImpactRecoveryObjectStatus: (setId: number, id: number, objectStatus: 2 | 4) =>
        api.patch<{ success: boolean; data: { id: number; objectStatus?: number; issueDescription?: string } }>(`/api/recovery-sets/${setId}/impact/recovery-objects/${id}`, { objectStatus }),
    updateImpactRecoveryObject: (setId: number, id: number, payload: { objectStatus?: 2 | 4; issueDescription?: string }) =>
        api.patch<{ success: boolean; data: { id: number } }>(`/api/recovery-sets/${setId}/impact/recovery-objects/${id}`, payload),
    getImpactRecoveryObjectStatus: (setId: number, params?: { search?: string; filter?: 'all' | 'amber' | 'services' | 'amber-services'; scenario?: number | null }) =>
        api.get<{ success: boolean; data: { data: ImpactRecoveryObjectStatusRow[]; scenariosAffected: number; affectedScenarioIds?: number[] } }>(`/api/recovery-sets/${setId}/impact/recovery-object-status`, { params: params ?? {} }),
}

export interface ImpactRecoveryObjectRow {
    id: number
    objectID: string
    description: string
    issueDescription: string
    objectStatus: number
}

export interface ImpactRecoveryObjectStatusRow {
    id: number
    objectID: string
    description: string
    analytics: number | null
}

export interface NotifyAllProgressEvent {
    type: 'progress'
    teamName?: string
    teamIndex: number
    totalTeams: number
    totalSentSoFar: number
    totalFailedSoFar: number
}

export interface MobilisationTaskAction {
    id: number
    action: string
    statusdatetime: string
    comments: string
    userName?: string | null
}

export type TechnicalTaskAction = MobilisationTaskAction

export interface MobilisationTaskRow {
    id: number
    recoveryMobilisationId?: number | null
    task: string
    status: string
    completedAt?: string
    completedBy?: string
    comments?: string
}

export interface MobilisationTeam {
    id: number
    teamName: string
    teamLead: string | null
    userCount: number
    status: 'DORMANT' | 'ACTIVE'
    standbyUsers?: number
    standbyUsersActivated?: number
    activationStatus?: string
    smsMessage?: string | null
    emailMessage?: string | null
    tasks?: MobilisationTaskRow[]
    checks?: TechnicalCheckRow[]
}

export interface TechnicalCheckRow {
    id: number
    recoveryTaskId?: number | null
    check: string
    status: string
    result: 'pending' | 'ok' | 'issue'
    completedAt?: string
    completedBy?: string
    comments?: string
}
